// -----------------------------------------------------------------------------
// Reparación de documentos: quién reemplazó a quién.
//
// Cuando un documento se repara con el CAF vigente no se corrige en el sitio:
// se emite uno NUEVO y el viejo queda marcado. Quedan entonces dos filas
// enlazadas por `notes`:
//
//   el viejo  → notes.caf_repair.replacement_document_*   (quién lo reemplazó)
//   el nuevo  → notes.repair_with_current_caf.source_*    (a quién reemplaza)
//
// Esto vive acá y no dentro de una pantalla porque lo usan la tabla y el
// detalle. Teniéndolo duplicado, una resolvía el vínculo y la otra no: en la
// lista salía la referencia y al abrir el documento no había ni rastro de cuál
// lo había reemplazado, que es justo lo que había que arreglar.
// -----------------------------------------------------------------------------

export function isDocumentRepaired(row) {
  const cafRepair = row?.notes?.caf_repair;
  const repairWithCurrentCaf = row?.notes?.repair_with_current_caf;
  const originalWasRepaired =
    cafRepair && typeof cafRepair === "object"
      ? Boolean(
          cafRepair.repaired_with_current_caf ||
            cafRepair.replacement_document_id ||
            cafRepair.replacement_document_uuid
        )
      : false;
  const currentDocumentIsRepair =
    repairWithCurrentCaf && typeof repairWithCurrentCaf === "object"
      ? Boolean(
          repairWithCurrentCaf.repaired_document ||
            repairWithCurrentCaf.repair_mode === "current_caf" ||
            repairWithCurrentCaf.source_document_id ||
            repairWithCurrentCaf.source_document_uuid
        )
      : false;
  return originalWasRepaired || currentDocumentIsRepair;
}

export function firstNonEmptyValue(...values) {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return null;
}

export function resolveRepairReference(row) {
  const cafRepair =
    row?.notes?.caf_repair && typeof row.notes.caf_repair === "object"
      ? row.notes.caf_repair
      : null;
  const repairWithCurrentCaf =
    row?.notes?.repair_with_current_caf &&
    typeof row.notes.repair_with_current_caf === "object"
      ? row.notes.repair_with_current_caf
      : null;
  const repairedDocument =
    repairWithCurrentCaf?.repaired_document &&
    typeof repairWithCurrentCaf.repaired_document === "object"
      ? repairWithCurrentCaf.repaired_document
      : null;

  const sourceId = firstNonEmptyValue(
    repairWithCurrentCaf?.source_document_id,
    repairWithCurrentCaf?.sourceDocumentId,
    repairWithCurrentCaf?.original_document_id,
    repairWithCurrentCaf?.originalDocumentId,
    repairedDocument?.id,
  );
  const sourceUuid = firstNonEmptyValue(
    repairWithCurrentCaf?.source_document_uuid,
    repairWithCurrentCaf?.sourceDocumentUuid,
    repairWithCurrentCaf?.original_document_uuid,
    repairWithCurrentCaf?.originalDocumentUuid,
    repairedDocument?.document_uuid,
    repairedDocument?.uuid,
  );
  const sourceFolio = firstNonEmptyValue(
    repairWithCurrentCaf?.source_folio,
    repairWithCurrentCaf?.sourceFolio,
    repairWithCurrentCaf?.original_folio,
    repairWithCurrentCaf?.originalFolio,
    repairedDocument?.folio,
  );
  const sourceTipo = firstNonEmptyValue(
    repairWithCurrentCaf?.source_tipo_dte,
    repairWithCurrentCaf?.sourceTipoDte,
    repairWithCurrentCaf?.original_tipo_dte,
    repairWithCurrentCaf?.originalTipoDte,
    repairedDocument?.tipo_dte,
  );
  if (sourceId || sourceUuid || sourceFolio || sourceTipo) {
    return {
      // `kind` es para el código; `label` es texto de pantalla y puede
      // reescribirse sin romper a quien decida en base a esto.
      kind: "reemplaza_a",
      label: "Documento reparado",
      id: sourceId,
      uuid: sourceUuid,
      folio: sourceFolio,
      tipoDte: sourceTipo,
    };
  }

  const replacementId = firstNonEmptyValue(
    cafRepair?.replacement_document_id,
    cafRepair?.replacementDocumentId,
    cafRepair?.repaired_document_id,
    cafRepair?.repairedDocumentId,
  );
  const replacementUuid = firstNonEmptyValue(
    cafRepair?.replacement_document_uuid,
    cafRepair?.replacementDocumentUuid,
    cafRepair?.repaired_document_uuid,
    cafRepair?.repairedDocumentUuid,
  );
  const replacementFolio = firstNonEmptyValue(
    cafRepair?.replacement_folio,
    cafRepair?.replacementFolio,
    cafRepair?.repaired_folio,
    cafRepair?.repairedFolio,
  );
  const replacementTipo = firstNonEmptyValue(
    cafRepair?.replacement_tipo_dte,
    cafRepair?.replacementTipoDte,
    cafRepair?.repaired_tipo_dte,
    cafRepair?.repairedTipoDte,
  );
  if (replacementId || replacementUuid || replacementFolio || replacementTipo) {
    return {
      kind: "reemplazado_por",
      label: "Nuevo documento",
      id: replacementId,
      uuid: replacementUuid,
      folio: replacementFolio,
      tipoDte: replacementTipo,
    };
  }

  return null;
}
